import React from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { FiClock } from 'react-icons/fi';
import { formatHhmm, parseHhmm } from './validateWindows';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

function commitHhmm(raw, onChange) {
  if (raw === '') {
    onChange('');
    return true;
  }
  const minutes = parseHhmm(raw);
  if (minutes === null) return false;
  onChange(formatHhmm(minutes));
  return true;
}

function TimeColumn({ label, items, selected, onPick }) {
  const listRef = React.useRef(null);
  React.useEffect(() => {
    const list = listRef.current;
    const el = list?.querySelector('[data-selected="true"]');
    if (!list || !el) return;
    list.scrollTop = el.offsetTop - list.clientHeight / 2 + el.offsetHeight / 2;
  }, [selected]);
  return (
    <div className="flex w-12 shrink-0 flex-col">
      <div className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-gray-400">{label}</div>
      <div
        ref={listRef}
        className="max-h-[6.5rem] overflow-x-hidden overflow-y-auto py-0.5 [scrollbar-width:thin] [scrollbar-color:rgb(203_213_225)_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300"
      >
        {items.map((item) => {
          const active = item === selected;
          return (
            <button
              key={item}
              type="button"
              data-selected={active ? 'true' : undefined}
              onClick={() => onPick(item)}
              className={`w-full py-0.5 pl-1.5 pr-2 text-center text-[11px] tabular-nums ${active ? 'bg-main-primary/10 font-semibold text-main-primary' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              {item}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function WindowTimeField({ value, onChange, error, ariaLabel }) {
  const [draft, setDraft] = React.useState(value || '');
  const minutes = parseHhmm(value);
  const hour = minutes === null ? '08' : String(Math.floor(minutes / 60)).padStart(2, '0');
  const minute = minutes === null ? '00' : String(minutes % 60).padStart(2, '0');

  React.useEffect(() => {
    setDraft(value || '');
  }, [value]);

  const pick = (nextHour, nextMinute, close) => {
    onChange(`${nextHour}:${nextMinute}`);
    close();
  };

  return (
    <Popover className="relative w-full min-w-0">
      {({ close }) => (
        <>
          <div className={`relative flex h-8 w-full items-center rounded-md border bg-white ${error ? 'border-red-400' : 'border-gray-200'}`}>
            <input
              aria-label={ariaLabel}
              value={draft}
              placeholder="HH:mm"
              onChange={(event) => {
                const next = event.target.value;
                setDraft(next);
                commitHhmm(next, onChange);
              }}
              onBlur={() => {
                if (!commitHhmm(draft, onChange)) setDraft(value || '');
              }}
              className="no-theme relative z-[1] !block !h-8 min-w-0 flex-1 !border-0 !bg-transparent !py-0 !pl-2 !pr-0 !text-[11px] !font-bold !tabular-nums !text-main-primary !shadow-none placeholder:!text-gray-400 focus:!ring-0"
            />
            <div className="h-8 w-7 shrink-0" />
            <PopoverButton
              aria-label={`${ariaLabel} picker`}
              className="absolute inset-0 flex items-center justify-end rounded-md text-main-primary hover:bg-main-primary/10 hover:bg-clip-padding"
            >
              <span className="flex h-8 w-7 items-center justify-center">
                <FiClock className="h-3.5 w-3.5" />
              </span>
            </PopoverButton>
          </div>
          <PopoverPanel
            anchor={{ to: 'bottom start', gap: 4 }}
            className="z-[10000] !w-[6.5rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
          >
            <div className="flex w-[6.5rem] divide-x divide-gray-100">
              <TimeColumn
                label="Hour"
                items={HOURS}
                selected={hour}
                onPick={(next) => onChange(`${next}:${minute}`)}
              />
              <TimeColumn
                label="Min"
                items={MINUTES}
                selected={minute}
                onPick={(next) => pick(hour, next, close)}
              />
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}
